import { z } from "zod";
import { IdSchema, IsoDateTimeSchema, pageSchema } from "./common.js";

/**
 * Design §13 — metering quality is explicit. Never fabricate an actual money
 * amount for a provider that cannot report one; say UNAVAILABLE instead.
 */
export const MeteringModeSchema = z.enum([
	"ACTUAL_MONEY",
	"ACTUAL_USAGE",
	"ESTIMATED_OPPORTUNITY_COST",
	"UNAVAILABLE",
]);
export type MeteringMode = z.infer<typeof MeteringModeSchema>;

export const CostEventSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	projectId: IdSchema.nullable(),
	runtimeId: IdSchema.nullable(),
	runtimeSpec: z.string().nullable(),
	sessionId: IdSchema.nullable(),
	executionId: IdSchema.nullable(),
	nodeExecutionId: IdSchema.nullable(),
	userId: IdSchema.nullable(),
	ucsId: IdSchema.nullable(),
	providerId: IdSchema.nullable(),
	modelId: IdSchema.nullable(),
	authenticationProfileId: IdSchema.nullable(),
	category: z.enum([
		"LLM_API",
		"SUBSCRIPTION_USAGE",
		"RUNTIME",
		"STORAGE",
		"TOOL",
		"NETWORK",
		"OTHER",
	]),
	meteringMode: MeteringModeSchema,
	/** reserved -> committed -> released. Design §20.17. */
	phase: z.enum(["RESERVED", "COMMITTED", "RELEASED"]),
	amount: z.number(),
	currency: z.string(),
	inputTokens: z.number().int().min(0),
	outputTokens: z.number().int().min(0),
	quantity: z.number().min(0),
	unit: z.string(),
	traceId: z.string(),
	occurredAt: IsoDateTimeSchema,
});
export type CostEvent = z.infer<typeof CostEventSchema>;

export const RecordCostEventRequestSchema = z.object({
	tenantId: IdSchema,
	projectId: IdSchema.nullable().default(null),
	runtimeId: IdSchema.nullable().default(null),
	runtimeSpec: z.string().nullable().default(null),
	sessionId: IdSchema.nullable().default(null),
	executionId: IdSchema.nullable().default(null),
	nodeExecutionId: IdSchema.nullable().default(null),
	userId: IdSchema.nullable().default(null),
	ucsId: IdSchema.nullable().default(null),
	providerId: IdSchema.nullable().default(null),
	modelId: IdSchema.nullable().default(null),
	authenticationProfileId: IdSchema.nullable().default(null),
	category: z.enum([
		"LLM_API",
		"SUBSCRIPTION_USAGE",
		"RUNTIME",
		"STORAGE",
		"TOOL",
		"NETWORK",
		"OTHER",
	]),
	meteringMode: MeteringModeSchema,
	phase: z.enum(["RESERVED", "COMMITTED", "RELEASED"]).default("COMMITTED"),
	amount: z.number().default(0),
	currency: z.string().default("USD"),
	inputTokens: z.number().int().min(0).default(0),
	outputTokens: z.number().int().min(0).default(0),
	quantity: z.number().min(0).default(0),
	unit: z.string().default("token"),
	traceId: z.string().default(""),
});
export type RecordCostEventRequest = z.infer<typeof RecordCostEventRequestSchema>;

export const CostSummarySchema = z.object({
	groupBy: z.string(),
	currency: z.string(),
	rows: z.array(
		z.object({
			key: z.string(),
			committedAmount: z.number(),
			reservedAmount: z.number(),
			releasedAmount: z.number(),
			inputTokens: z.number().int(),
			outputTokens: z.number().int(),
			eventCount: z.number().int(),
			/** Kept split so unmeasurable usage is never merged into money. */
			meteringModes: z.record(z.string(), z.number().int()),
		}),
	),
});
export type CostSummary = z.infer<typeof CostSummarySchema>;

export const AuditEventSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema,
	actorType: z.string(),
	actorId: IdSchema.nullable(),
	action: z.string(),
	resourceType: z.string(),
	resourceId: IdSchema.nullable(),
	reason: z.string().nullable(),
	outcome: z.enum(["ALLOWED", "DENIED", "FAILED"]),
	/** Redacted metadata only — never secret material. */
	metadata: z.record(z.string(), z.unknown()),
	traceId: z.string(),
	occurredAt: IsoDateTimeSchema,
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

export const RecordAuditEventRequestSchema = z.object({
	tenantId: IdSchema,
	actorType: z.string(),
	actorId: IdSchema.nullable().default(null),
	action: z.string().min(1),
	resourceType: z.string().min(1),
	resourceId: IdSchema.nullable().default(null),
	reason: z.string().nullable().default(null),
	outcome: z.enum(["ALLOWED", "DENIED", "FAILED"]).default("ALLOWED"),
	metadata: z.record(z.string(), z.unknown()).default({}),
	traceId: z.string().default(""),
});
export type RecordAuditEventRequest = z.infer<typeof RecordAuditEventRequestSchema>;

export const ErrorEventSchema = z.object({
	id: IdSchema,
	tenantId: IdSchema.nullable(),
	service: z.string(),
	severity: z.enum(["INFO", "WARN", "ERROR", "FATAL"]),
	errorClass: z.string(),
	message: z.string(),
	executionId: IdSchema.nullable(),
	nodeExecutionId: IdSchema.nullable(),
	sessionId: IdSchema.nullable(),
	providerId: IdSchema.nullable(),
	traceId: z.string(),
	metadata: z.record(z.string(), z.unknown()),
	occurredAt: IsoDateTimeSchema,
});
export type ErrorEvent = z.infer<typeof ErrorEventSchema>;

export const RecordErrorEventRequestSchema = z.object({
	tenantId: IdSchema.nullable().default(null),
	service: z.string().min(1),
	severity: z.enum(["INFO", "WARN", "ERROR", "FATAL"]).default("ERROR"),
	errorClass: z.string().min(1),
	message: z.string().min(1),
	executionId: IdSchema.nullable().default(null),
	nodeExecutionId: IdSchema.nullable().default(null),
	sessionId: IdSchema.nullable().default(null),
	providerId: IdSchema.nullable().default(null),
	traceId: z.string().default(""),
	metadata: z.record(z.string(), z.unknown()).default({}),
});
export type RecordErrorEventRequest = z.infer<typeof RecordErrorEventRequestSchema>;

export const LlmUsageProviderSchema = z.enum(["claude", "codex"]);
export type LlmUsageProvider = z.infer<typeof LlmUsageProviderSchema>;

export const RecordLlmUsageRequestSchema = z
	.object({
		sourceExecId: IdSchema,
		tenantId: IdSchema,
		projectId: IdSchema,
		runtimeId: IdSchema,
		workOrderId: IdSchema.nullable().default(null),
		sessionId: IdSchema.nullable().default(null),
		provider: LlmUsageProviderSchema,
		startedAt: IsoDateTimeSchema,
		endedAt: IsoDateTimeSchema,
		durationMs: z.number().int().min(0),
	})
	.superRefine((value, ctx) => {
		const startedAt = Date.parse(value.startedAt);
		const endedAt = Date.parse(value.endedAt);
		if (!Number.isFinite(startedAt)) {
			ctx.addIssue({ code: "custom", path: ["startedAt"], message: "Invalid date-time" });
		}
		if (!Number.isFinite(endedAt)) {
			ctx.addIssue({ code: "custom", path: ["endedAt"], message: "Invalid date-time" });
		}
		if (Number.isFinite(startedAt) && Number.isFinite(endedAt) && endedAt < startedAt) {
			ctx.addIssue({
				code: "custom",
				path: ["endedAt"],
				message: "endedAt must be on or after startedAt",
			});
		}
	});
export type RecordLlmUsageRequest = z.infer<typeof RecordLlmUsageRequestSchema>;

export const LlmUsageRecordSchema = RecordLlmUsageRequestSchema.and(
	z.object({
		id: IdSchema,
		recordedAt: IsoDateTimeSchema,
	}),
);
export type LlmUsageRecord = z.infer<typeof LlmUsageRecordSchema>;

export const LlmUsagePeriodSchema = z.enum(["today", "currentMonth", "previousMonth", "all"]);
export type LlmUsagePeriod = z.infer<typeof LlmUsagePeriodSchema>;

export const LlmUsageProviderSummarySchema = z.object({
	provider: LlmUsageProviderSchema,
	activeSeconds: z.number().min(0),
	execCount: z.number().int().min(0),
});

export const LlmUsageAssignmentSummarySchema = z.object({
	workOrderId: IdSchema.nullable(),
	sessionId: IdSchema.nullable(),
	provider: LlmUsageProviderSchema,
	activeSeconds: z.number().min(0),
	execCount: z.number().int().min(0),
});

export const LlmUsageDailySummarySchema = z.object({
	date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
	activeSeconds: z.number().min(0),
	thresholdSeconds: z.number().positive(),
	thresholdExceeded: z.boolean(),
});

export const LlmUsageSummarySchema = z.object({
	period: LlmUsagePeriodSchema,
	timezone: z.literal("Asia/Tokyo"),
	thresholdSeconds: z.number().positive(),
	providers: z.array(LlmUsageProviderSummarySchema),
	assignments: z.array(LlmUsageAssignmentSummarySchema),
	days: z.array(LlmUsageDailySummarySchema),
});
export type LlmUsageSummary = z.infer<typeof LlmUsageSummarySchema>;

export const CostEventPageSchema = pageSchema(CostEventSchema);
export const AuditEventPageSchema = pageSchema(AuditEventSchema);
export const ErrorEventPageSchema = pageSchema(ErrorEventSchema);
